import { lazy, Suspense, useEffect, useState } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import ProtectedRoute from './components/ProtectedRoute';
import AccessGate from './components/AccessGate';
import NavBar from './components/NavBar';
import { ToastProvider } from './components/Toast';
import { ConfirmProvider } from './components/ConfirmDialog';
import { ThemeProvider } from './context/ThemeContext';
import { ProjectProvider } from './context/ProjectContext';

// Route-level code splitting — each page is its own chunk, fetched on first
// navigation instead of all being bundled into the initial app load.
const Dashboard                 = lazy(() => import('./pages/Dashboard'));
const MySubmissionsPage         = lazy(() => import('./pages/MySubmissionsPage'));
const AssignedForReviewPage     = lazy(() => import('./pages/AssignedForReviewPage'));
const AllSessionsPage           = lazy(() => import('./pages/AllSessionsPage'));
const ReviewPage                = lazy(() => import('./pages/ReviewPage'));
const SettingsLayout            = lazy(() => import('./pages/settings/SettingsLayout'));
const SettingsHub               = lazy(() => import('./pages/settings/SettingsHub'));
const PushTargetSettingsPage    = lazy(() => import('./pages/settings/PushTargetSettingsPage'));
const LlmSettingsPage           = lazy(() => import('./pages/settings/LlmSettingsPage'));
const GitSettingsPage           = lazy(() => import('./pages/settings/GitSettingsPage'));
const UserManagementPage        = lazy(() => import('./pages/settings/UserManagementPage'));
const MySettingsHub             = lazy(() => import('./pages/settings/MySettingsHub'));
const MyLlmSettingsPage         = lazy(() => import('./pages/settings/MyLlmSettingsPage'));
const MyGitSettingsPage         = lazy(() => import('./pages/settings/MyGitSettingsPage'));
const MyTestLinkSettingsPage    = lazy(() => import('./pages/settings/MyTestLinkSettingsPage'));
const ProjectsPage              = lazy(() => import('./pages/ProjectsPage'));
const ProjectDetailPage         = lazy(() => import('./pages/ProjectDetailPage'));
const GeneratorPage             = lazy(() => import('./pages/GeneratorPage'));
const ApiTestingPage            = lazy(() => import('./pages/ApiTestingPage'));
const WebAutomationPage         = lazy(() => import('./pages/WebAutomationPage'));
const McpCodegenPage            = lazy(() => import('./pages/McpCodegenPage'));
const ReleasesPage              = lazy(() => import('./pages/ReleasesPage'));
const ReleaseDetailPage         = lazy(() => import('./pages/ReleaseDetailPage'));

function PageLoading() {
  return <div className="page-loading"><span className="spinner" />Loading…</div>;
}

// Keeps GeneratorPage and ApiTestingPage mounted once visited so that
// uploaded files and form state survive navigation to other pages — but,
// unlike before, they aren't mounted (or fetched) until the user actually
// visits one of those routes for the first time.
function KeepAlivePages() {
  const { pathname } = useLocation();
  const [visited, setVisited] = useState(() => ({
    generator:  pathname === '/generator',
    apiTesting: pathname === '/api-testing',
  }));

  useEffect(() => {
    if (pathname === '/generator' && !visited.generator) {
      setVisited(v => ({ ...v, generator: true }));
    } else if (pathname === '/api-testing' && !visited.apiTesting) {
      setVisited(v => ({ ...v, apiTesting: true }));
    }
  }, [pathname, visited]);

  return (
    <Suspense fallback={null}>
      {visited.generator && (
        <div style={{ display: pathname === '/generator' ? 'block' : 'none' }}><GeneratorPage /></div>
      )}
      {visited.apiTesting && (
        <div style={{ display: pathname === '/api-testing' ? 'block' : 'none' }}><ApiTestingPage /></div>
      )}
    </Suspense>
  );
}

export default function App() {
  return (
    <ThemeProvider>
    <ProtectedRoute>
      <AccessGate>
      <ToastProvider>
      <ConfirmProvider>
      <ProjectProvider>
      <div className="app-layout">
        <NavBar />
        <main className="app-main">
        <div className="app-main-inner">
          <KeepAlivePages />
          <Suspense fallback={<PageLoading />}>
          <Routes>
            {/* Dashboard layout with sub-nav */}
            <Route path="/" element={<Dashboard />}>
              <Route index element={<Navigate to="my-submissions" replace />} />
              <Route path="my-submissions" element={<MySubmissionsPage />} />
              <Route path="assigned"       element={<AssignedForReviewPage />} />
              <Route path="all-sessions"   element={<AllSessionsPage />} />
            </Route>

            {/* Placeholder routes so the * redirect doesn't fire on these paths */}
            <Route path="/generator"   element={null} />
            <Route path="/api-testing" element={null} />

            <Route path="/sessions/:sessionId" element={<ReviewPage />} />
            <Route path="/web-automation"      element={<WebAutomationPage />} />
            <Route path="/web-automation/mcp/:jobId" element={<McpCodegenPage />} />
            <Route path="/releases"     element={<ReleasesPage />} />
            <Route path="/releases/:id" element={<ReleaseDetailPage />} />
            <Route path="/settings" element={<SettingsLayout />}>
              <Route index               element={<SettingsHub />} />
              <Route path="push-target"  element={<PushTargetSettingsPage />} />
              <Route path="llm"          element={<LlmSettingsPage />} />
              <Route path="git"          element={<GitSettingsPage />} />
              <Route path="users"        element={<UserManagementPage />} />
              <Route path="projects"     element={<ProjectsPage />} />
              <Route path="projects/:id" element={<ProjectDetailPage />} />
              <Route path="profile"          element={<MySettingsHub />} />
              <Route path="profile/llm"      element={<MyLlmSettingsPage />} />
              <Route path="profile/git"      element={<MyGitSettingsPage />} />
              <Route path="profile/testlink" element={<MyTestLinkSettingsPage />} />
            </Route>
            <Route path="*"                    element={<Navigate to="/my-submissions" replace />} />
          </Routes>
          </Suspense>
        </div>
        </main>
      </div>
      </ProjectProvider>
      </ConfirmProvider>
      </ToastProvider>
      </AccessGate>
    </ProtectedRoute>
    </ThemeProvider>
  );
}
